/**
 * IsochroneControl.tsx — Off·5·10·15 drive-time segmented control (T-135 / BEAD-0075)
 *
 * Opt-in by design (default Off): firing a GeoRoutes CalculateIsolines call on every
 * pin drop is wasteful and usually unwanted, so the isochrone only renders once the
 * user explicitly picks a duration here. Pure presentation — the parent (MapShell)
 * owns the `isochroneMinutes` state and passes the chosen value to IsochroneLayer.
 *
 * A glass map-overlay (matches EVLegend / the other map chrome), theme-aware via the
 * existing design tokens (bg-primary / text-muted-foreground). The "min" unit lives in
 * the header so each button's accessible name is just its value (Off / 5 / 10 / 15).
 */
export interface IsochroneControlProps {
  minutes: 5 | 10 | 15 | null
  onChange: (minutes: 5 | 10 | 15 | null) => void
}

const OPTIONS: { label: string; value: 5 | 10 | 15 | null }[] = [
  { label: 'Off', value: null },
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
]

export function IsochroneControl({ minutes, onChange }: IsochroneControlProps) {
  return (
    <div className="glass rounded-lg border border-border/60 shadow-md px-2.5 py-2 text-[10px] w-48">
      <p className="font-semibold text-muted-foreground mb-1">⏱ Drive-time area (min)</p>
      <div className="flex gap-1" role="group" aria-label="Drive-time isochrone duration">
        {OPTIONS.map((opt) => {
          const active = minutes === opt.value
          return (
            <button
              key={opt.label}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(opt.value)}
              className={
                'flex-1 rounded px-1.5 py-1 text-[11px] font-medium transition-colors ' +
                (active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-transparent text-muted-foreground hover:bg-muted')
              }
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
